/**
 * useModalState Hook
 *
 * Generic hook for managing modal open/close state with optional payload data.
 * Useful for modals that need to pass data when opening (e.g., confirmation modals).
 *
 * @example
 * ```tsx
 * // Simple modal without payload
 * const addProductModal = useModalState();
 * <Button onClick={addProductModal.open}>Add Product</Button>
 * <Modal open={addProductModal.isOpen} onClose={addProductModal.close} />
 *
 * // Modal with typed payload
 * interface ProductPayload { productId: string; productTitle: string; }
 * const navigationModal = useModalState<ProductPayload>();
 * navigationModal.open({ productId: '123', productTitle: 'Widget' });
 * // navigationModal.data?.productId is typed
 * ```
 */

import { useState, useCallback } from 'react';

/**
 * Return type for useModalState hook
 */
export interface UseModalStateReturn<T = undefined> {
  /** Whether the modal is currently open */
  isOpen: boolean;

  /** Payload data passed when opening (null if closed or no data) */
  data: T | null;

  /** Open the modal, optionally with payload data */
  open: T extends undefined ? () => void : (payload: T) => void;

  /** Close the modal and clear payload data */
  close: () => void;

  /** Toggle modal state (without payload) */
  toggle: () => void;
}

/**
 * Hook for managing modal open/close state with optional typed payload
 *
 * @param initialOpen - Whether modal starts open (default: false)
 * @returns Modal state and handlers
 */
export function useModalState<T = undefined>(
  initialOpen = false
): UseModalStateReturn<T> {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [data, setData] = useState<T | null>(null);

  const open = useCallback((payload?: T) => {
    setData(payload ?? null);
    setIsOpen(true);
  }, []) as UseModalStateReturn<T>['open'];

  const close = useCallback(() => {
    setIsOpen(false);
    // Clear data after a brief delay to allow closing animation
    setTimeout(() => setData(null), 150);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        // Closing - clear data
        setTimeout(() => setData(null), 150);
      }
      return !prev;
    });
  }, []);

  return {
    isOpen,
    data,
    open,
    close,
    toggle,
  };
}

/**
 * Convenience type for modal with product navigation payload
 */
export interface ProductNavigationPayload {
  productId: string;
  productTitle: string;
  shopDomain: string;
}

/**
 * Convenience type for modal with pre-selected product ID
 */
export interface ProductSelectionPayload {
  preSelectedProductId?: string;
}
